import React, { useMemo, useState } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  ArrowDownWideNarrow,
  CalendarDays,
  Check,
  ChevronDown,
  CopyCheck,
  FormInput,
  Info,
  Layers3,
  LayoutPanelTop,
  Palette,
  Rows3,
  Search,
  SlidersHorizontal,
  TableProperties,
  XCircle,
} from 'lucide-react';

const formatWeekdayDate = (weekday, day, month, year) => `${weekday}, ${String(day).padStart(2, '0')} ${month} ${year}`;
const formatRupeeCompact = (value) => {
  const abs = Math.abs(value);
  if (abs >= 1000000) return `Rs. ${(value / 1000000).toFixed(2)}M`;
  if (abs >= 1000) return `Rs. ${(value / 1000).toFixed(2)}K`;
  return `Rs. ${Math.round(value)}`;
};

const TAB_ITEMS = [
  { id: 'foundation', label: 'Foundation', icon: Palette },
  { id: 'flow', label: 'Page Flow', icon: LayoutPanelTop },
  { id: 'filters', label: 'Filters', icon: SlidersHorizontal },
  { id: 'cards', label: 'Summary Cards', icon: Layers3 },
  { id: 'tables', label: 'Tables', icon: TableProperties },
  { id: 'calendar', label: 'Calendar', icon: CalendarDays },
  { id: 'forms', label: 'Forms', icon: FormInput },
  { id: 'popups', label: 'Popups', icon: CopyCheck },
  { id: 'feedback', label: 'Feedback', icon: AlertTriangle },
];

const COLOR_TOKENS = [
  { name: 'Primary', value: '#16a34a', classes: 'bg-green-600 text-white' },
  { name: 'Primary Soft', value: '#dcfce7', classes: 'bg-green-100 text-green-800' },
  { name: 'Accent', value: '#f97316', classes: 'bg-orange-500 text-white' },
  { name: 'Warning', value: '#f59e0b', classes: 'bg-amber-500 text-white' },
  { name: 'Danger', value: '#ef4444', classes: 'bg-red-500 text-white' },
  { name: 'Info', value: '#2563eb', classes: 'bg-blue-600 text-white' },
  { name: 'Surface', value: '#ffffff', classes: 'bg-white text-gray-800 border border-gray-200' },
  { name: 'Canvas', value: '#f3f4f6', classes: 'bg-gray-100 text-gray-700' },
];

const TYPE_SCALE = [
  { label: 'Display', className: 'text-3xl font-bold text-gray-900', preview: 'UI system heading' },
  { label: 'Page Title', className: 'text-2xl font-bold text-gray-800', preview: 'Page title' },
  { label: 'Section Title', className: 'text-lg font-semibold text-gray-800', preview: 'Section title' },
  { label: 'Card Value', className: 'text-2xl font-bold text-gray-900', preview: '91.56K' },
  { label: 'Body', className: 'text-sm text-gray-600', preview: 'Consistent body copy for filters, help text, and labels.' },
  { label: 'Caption', className: 'text-xs text-gray-500', preview: 'Subtext / helper / status context' },
];

const FILTER_PRESETS = ['Monthly', 'Annually'];
const FULL_FILTER_PRESETS = ['Daily', 'Weekly', 'Monthly', 'Annually', 'Custom'];
const YEAR_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const PreviewBlock = ({ title, caption, children }) => (
  <section className="rounded-2xl border border-gray-200 bg-white shadow-sm">
    <div className="border-b border-gray-100 px-5 py-4">
      <h3 className="text-base font-semibold text-gray-800">{title}</h3>
      {caption && <p className="mt-1 text-sm text-gray-500">{caption}</p>}
    </div>
    <div className="p-5">{children}</div>
  </section>
);

const StatusChip = ({ tone = 'neutral', children }) => {
  const toneClass =
    tone === 'success' ? 'bg-green-100 text-green-700'
      : tone === 'warning' ? 'bg-amber-100 text-amber-700'
      : tone === 'danger' ? 'bg-red-100 text-red-700'
      : tone === 'info' ? 'bg-blue-100 text-blue-700'
      : 'bg-gray-100 text-gray-700';
  return <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${toneClass}`}>{children}</span>;
};

const SummaryCard = ({ label, value, helper, tone = 'neutral' }) => {
  const toneClass =
    tone === 'success' ? 'from-green-50 to-green-100 border-green-200'
      : tone === 'warning' ? 'from-amber-50 to-amber-100 border-amber-200'
      : tone === 'danger' ? 'from-red-50 to-red-100 border-red-200'
      : tone === 'info' ? 'from-blue-50 to-blue-100 border-blue-200'
      : 'from-gray-50 to-white border-gray-200';

  return (
    <div className={`rounded-2xl border bg-gradient-to-br ${toneClass} p-4`}>
      <p className="text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">{label}</p>
      <p className="mt-2 text-2xl font-bold text-gray-900">{value}</p>
      <p className="mt-1 text-xs text-gray-500">{helper}</p>
    </div>
  );
};

const FilterPill = ({ active = false, children }) => (
  <button
    type="button"
    className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
      active ? 'bg-green-600 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-green-300'
    }`}
  >
    {children}
  </button>
);

const TableHeaderCell = ({ label, active = false }) => (
  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">
    <button type="button" className={`inline-flex items-center gap-1 ${active ? 'text-green-700' : 'text-gray-500 hover:text-gray-700'}`}>
      {label}
      <ChevronDown size={14} className={active ? 'rotate-180' : ''} />
    </button>
  </th>
);

const CalendarDayCard = ({ day, amount, tone = 'normal', note }) => {
  const toneClass =
    tone === 'today' ? 'bg-blue-50 border-blue-200 ring-2 ring-blue-300'
      : tone === 'holiday' ? 'bg-amber-50 border-amber-300'
      : tone === 'pastEmpty' ? 'bg-gray-300 border-gray-400'
      : tone === 'future' ? 'bg-gray-50 border-gray-200 opacity-70'
      : 'bg-white border-gray-200';

  return (
    <div className={`min-h-[110px] rounded-2xl border p-3 ${toneClass}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-gray-500">{day}</span>
        {note && <StatusChip tone={tone === 'holiday' ? 'warning' : 'info'}>{note}</StatusChip>}
      </div>
      <p className="mt-4 text-xl font-bold text-gray-900">{amount}</p>
      <p className="mt-1 text-xs text-gray-500">Daily aggregate example</p>
    </div>
  );
};

const FormField = ({ label, helper, children }) => (
  <div className="space-y-2">
    <label className="block text-sm font-medium text-gray-700">{label}</label>
    {children}
    {helper && <p className="text-xs text-gray-500">{helper}</p>}
  </div>
);

const UITemplates = () => {
  const [activeTab, setActiveTab] = useState('foundation');

  const tabs = useMemo(() => TAB_ITEMS, []);

  return (
    <div className="space-y-5">
      <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-green-700">Local Approval Gallery</p>
            <h1 className="mt-2 text-2xl font-bold text-gray-900">UI Templates</h1>
            <p className="mt-2 max-w-3xl text-sm text-gray-600">
              This page is the staging area for standardizing the app UI without losing behavior. We will approve each
              component family here first, then roll it into real screens one at a time.
            </p>
          </div>
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
            <p className="text-sm font-semibold text-amber-800">Preview only</p>
            <p className="mt-1 text-xs text-amber-700">No production screens will be switched to these components until you approve them.</p>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-100 px-4 py-3">
          <div className="flex flex-wrap gap-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition ${
                    isActive ? 'bg-green-600 text-white shadow-sm' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Icon size={16} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-4 sm:p-5">
          {activeTab === 'foundation' && (
            <div className="space-y-5">
              <PreviewBlock
                title="Typography Scale"
                caption="This is the shared text skeleton we will reuse across pages for consistency."
              >
                <div className="space-y-4">
                  {TYPE_SCALE.map((item) => (
                    <div key={item.label} className="grid gap-2 rounded-2xl border border-gray-100 px-4 py-3 md:grid-cols-[11rem_minmax(0,1fr)]">
                      <p className="text-xs font-semibold uppercase tracking-[0.12em] text-gray-400">{item.label}</p>
                      <p className={item.className}>{item.preview}</p>
                    </div>
                  ))}
                </div>
              </PreviewBlock>

              <PreviewBlock
                title="Color Tokens"
                caption="Global colors to reuse for cards, buttons, chips, alerts, and charts."
              >
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  {COLOR_TOKENS.map((token) => (
                    <div key={token.name} className="rounded-2xl border border-gray-200 p-3">
                      <div className={`h-20 rounded-xl ${token.classes}`} />
                      <div className="mt-3">
                        <p className="text-sm font-semibold text-gray-800">{token.name}</p>
                        <p className="text-xs text-gray-500">{token.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </PreviewBlock>

              <PreviewBlock
                title="Date and Number Display"
                caption="These are the rules we’ll standardize across filters, cards, tables, and calendars."
              >
                <div className="grid gap-4 md:grid-cols-4">
                  <div className="rounded-2xl bg-gray-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.1em] text-gray-500">Foundation Date Format</p>
                    <p className="mt-2 text-lg font-semibold text-gray-900">{formatWeekdayDate('Mon', 11, 'May', 2026)}</p>
                    <p className="mt-1 text-xs text-gray-500">Weekday always shown first because business day matters more than raw date.</p>
                  </div>
                  <div className="rounded-2xl bg-gray-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.1em] text-gray-500">Compact Amount Format</p>
                    <p className="mt-2 text-lg font-semibold text-gray-900">{formatRupeeCompact(1010)}</p>
                    <p className="mt-1 text-xs text-gray-500">Examples: {formatRupeeCompact(9230)} · {formatRupeeCompact(9230000)}</p>
                  </div>
                  <div className="rounded-2xl bg-gray-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.1em] text-gray-500">Calendar Rule</p>
                    <p className="mt-2 text-lg font-semibold text-gray-900">Month starts Monday</p>
                    <p className="mt-1 text-xs text-gray-500">Calendar filters will be limited to Month and Year.</p>
                  </div>
                  <div className="rounded-2xl bg-gray-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.1em] text-gray-500">Time Format</p>
                    <p className="mt-2 text-lg font-semibold text-gray-900">23:57</p>
                    <p className="mt-1 text-xs text-gray-500">Always 24-hour time. No AM/PM. Inputs should allow direct typing instead of cycle selectors.</p>
                  </div>
                </div>
                </PreviewBlock>

                <PreviewBlock
                  title="Reuse Rule"
                  caption="When the same UI is needed on another page, we remodel and reuse the same component instead of duplicating code."
                >
                  <div className="grid gap-4 lg:grid-cols-2">
                    <div className="rounded-2xl border border-green-200 bg-green-50 p-4">
                      <div className="flex items-center gap-2 text-green-700">
                        <CopyCheck size={18} />
                        <p className="font-semibold">Do this</p>
                      </div>
                      <p className="mt-2 text-sm text-green-700">
                        Reuse one component for cases like Staff Salary and Expenses Salary, or a historical POS launched for an old business date.
                      </p>
                    </div>
                    <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
                      <div className="flex items-center gap-2 text-red-700">
                        <AlertTriangle size={18} />
                        <p className="font-semibold">Avoid this</p>
                      </div>
                      <p className="mt-2 text-sm text-red-700">
                        Do not create duplicate pages that drift apart. Differences should come from props, mode, and permissions.
                      </p>
                    </div>
                  </div>
                </PreviewBlock>
              </div>
            )}

            {activeTab === 'flow' && (
              <div className="space-y-5">
                <PreviewBlock
                  title="Standard Page Order"
                  caption="Every data-heavy page should follow this same order unless there is a very strong reason not to."
                >
                  <div className="space-y-3">
                    {[
                      '1. Calendar or Table view toggle',
                      '2. Date and time filter block',
                      '3. Search bar',
                      '4. Page tabs',
                      '5. Other respective filters and action buttons',
                      '6. Summary cards',
                      '7. Data area',
                    ].map((line, index) => (
                      <div key={line} className={`rounded-2xl border px-4 py-3 ${index === 0 ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-white'}`}>
                        <p className="text-sm font-semibold text-gray-800">{line}</p>
                      </div>
                    ))}
                  </div>
                </PreviewBlock>

                <PreviewBlock
                  title="Page Skeleton Preview"
                  caption="This is the base page scaffold we'll reuse when standardizing screen by screen."
                >
                  <div className="rounded-3xl border border-gray-200 bg-gray-50 p-4">
                    <div className="rounded-2xl border border-gray-200 bg-white p-4">
                      <div className="flex gap-2">
                        <FilterPill active>Calendar</FilterPill>
                        <FilterPill>Table</FilterPill>
                      </div>
                    </div>
                    <div className="mt-3 rounded-2xl border border-gray-200 bg-white p-4">
                      <div className="flex gap-2">
                        <StatusChip tone="info">Tab 1</StatusChip>
                        <StatusChip>Tab 2</StatusChip>
                        <StatusChip>Tab 3</StatusChip>
                      </div>
                    </div>
                    <div className="mt-3 rounded-2xl border border-gray-200 bg-white p-4 text-sm text-gray-700">Date Filter</div>
                    <div className="mt-3 rounded-2xl border border-gray-200 bg-white p-4 text-sm text-gray-700">Search Bar</div>
                    <div className="mt-3 rounded-2xl border border-gray-200 bg-white p-4 text-sm text-gray-700">Page Tabs</div>
                    <div className="mt-3 rounded-2xl border border-gray-200 bg-white p-4 text-sm text-gray-700">Other Filters / Actions</div>
                    <div className="mt-3 rounded-2xl border border-gray-200 bg-white p-4 text-sm text-gray-700">Summary Cards</div>
                    <div className="mt-3 rounded-2xl border border-gray-200 bg-white p-4 text-sm text-gray-700">Data</div>
                  </div>
                </PreviewBlock>
              </div>
            )}

            {activeTab === 'filters' && (
            <div className="space-y-5">
              <PreviewBlock
                title="Unified Filter Bar"
                caption="This is the base filter pattern for date presets, range selection, staff/item filters, and search."
              >
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    {FULL_FILTER_PRESETS.map((preset) => (
                      <FilterPill key={preset} active={preset === 'Monthly'}>{preset}</FilterPill>
                    ))}
                  </div>
                  <div className="grid gap-3 xl:grid-cols-[12rem_9rem_12rem_9rem_1fr]">
                    <input className="rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-green-500 focus:outline-none" value={formatWeekdayDate('Fri', 1, 'May', 2026)} readOnly />
                    <input className="rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-green-500 focus:outline-none" value="06:00" readOnly />
                    <input className="rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-green-500 focus:outline-none" value={formatWeekdayDate('Mon', 1, 'Jun', 2026)} readOnly />
                    <input className="rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-green-500 focus:outline-none" value="05:59" readOnly />
                    <button type="button" className="inline-flex items-center justify-between rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm">
                      All Staff
                      <ChevronDown size={16} />
                    </button>
                    <div className="relative xl:col-span-5">
                      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input className="w-full rounded-xl border border-gray-200 py-2 pl-9 pr-3 text-sm text-gray-700 shadow-sm focus:border-green-500 focus:outline-none" placeholder="Search records..." />
                    </div>
                  </div>
                  <p className="rounded-xl border border-dashed border-gray-200 px-3 py-2 text-xs text-gray-500">
                    Monthly stores the business end as the last business date, but displays the next calendar day at 05:59. Custom previous/next shifts by the selected business-day span.
                  </p>
                  <p className="text-xs text-gray-500">Selected range: 06:00 {formatWeekdayDate('Fri', 1, 'May', 2026)} to 05:59 {formatWeekdayDate('Mon', 1, 'Jun', 2026)}</p>
                </div>
              </PreviewBlock>
            </div>
          )}

          {activeTab === 'cards' && (
            <div className="space-y-5">
              <PreviewBlock
                title="Summary Cards"
                caption="These are the card shells we’ll standardize across Sales, Reports, Reconciliation, Staff, and Expenses."
              >
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <SummaryCard label="Total Sale" value={formatRupeeCompact(91560)} helper="Month-to-date example" tone="success" />
                  <SummaryCard label="Expected Cash" value={formatRupeeCompact(99510)} helper="Reconciliation example" tone="warning" />
                  <SummaryCard label="Salary Due" value={formatRupeeCompact(48000)} helper={`Due on ${formatWeekdayDate('Sat', 20, 'Jun', 2026)}`} tone="info" />
                  <SummaryCard label="Deduction" value={formatRupeeCompact(1500)} helper="Absentee example" tone="danger" />
                </div>
              </PreviewBlock>
            </div>
          )}

          {activeTab === 'tables' && (
            <div className="space-y-5">
              <PreviewBlock
                title="Standard Table + Sortable Header"
                caption="This is the reusable table shell for search results, reports, staff ledgers, and audit-style lists. Every column will remain sortable."
              >
                <div className="overflow-x-auto rounded-2xl border border-gray-200">
                  <table className="min-w-full bg-white">
                    <thead className="bg-gray-50">
                      <tr>
                        <TableHeaderCell label="Staff" />
                        <TableHeaderCell label="Date" active />
                        <TableHeaderCell label="Type" />
                        <TableHeaderCell label="Amount" />
                        <TableHeaderCell label="Status" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 text-sm">
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-800">Raees</td>
                        <td className="px-4 py-3 text-gray-600">{formatWeekdayDate('Fri', 29, 'May', 2026)}</td>
                        <td className="px-4 py-3 text-gray-600">Advance</td>
                        <td className="px-4 py-3 font-semibold text-gray-900">{formatRupeeCompact(1000)}</td>
                        <td className="px-4 py-3"><StatusChip tone="info">Approved</StatusChip></td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-800">Sayam</td>
                        <td className="px-4 py-3 text-gray-600">{formatWeekdayDate('Thu', 28, 'May', 2026)}</td>
                        <td className="px-4 py-3 text-gray-600">Salary</td>
                        <td className="px-4 py-3 font-semibold text-gray-900">{formatRupeeCompact(39500)}</td>
                        <td className="px-4 py-3"><StatusChip tone="success">Paid</StatusChip></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="mt-4 rounded-2xl border border-green-200 bg-green-50 p-4">
                  <div className="flex items-center gap-2 text-green-700">
                    <ArrowDownWideNarrow size={16} />
                    <p className="text-sm font-semibold">Rule</p>
                  </div>
                  <p className="mt-1 text-sm text-green-700">Every column in every table stays sortable. No exceptions for summary, search, report, or popup tables.</p>
                </div>
              </PreviewBlock>
            </div>
          )}

            {activeTab === 'calendar' && (
              <div className="space-y-5">
                <PreviewBlock
                  title="Calendar Filters"
                  caption="All calendar views will only use Month and Year."
                >
                  <div className="flex flex-wrap gap-2">
                    {FILTER_PRESETS.map((preset) => (
                      <FilterPill key={`calendar-${preset}`} active={preset === 'Month'}>{preset}</FilterPill>
                    ))}
                  </div>
                </PreviewBlock>

                <PreviewBlock
                  title="Calendar Day Cards"
                  caption="Past empty days, future days, holidays, today, and active data cards will all come from one shared visual language. Month views will start on Monday."
              >
                <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-4">
                  <CalendarDayCard day="Mon 12" amount={formatRupeeCompact(35010)} />
                  <CalendarDayCard day="Tue 13" amount={formatRupeeCompact(0)} tone="pastEmpty" />
                  <CalendarDayCard day="Wed 14" amount={formatRupeeCompact(52400)} tone="today" note="Today" />
                  <CalendarDayCard day="Thu 15" amount={formatRupeeCompact(0)} tone="future" />
                    <CalendarDayCard day="Fri 16" amount={formatRupeeCompact(12500)} tone="holiday" note="Holiday" />
                  </div>
                </PreviewBlock>

                <PreviewBlock
                  title="Year View"
                  caption="All Year calendar views will show a grid of 12 months."
                >
                  <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-4">
                    {YEAR_MONTHS.map((month, index) => (
                      <div key={month} className={`rounded-2xl border p-4 ${index === 4 ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-white'}`}>
                        <p className="text-sm font-semibold text-gray-800">{month}</p>
                        <p className="mt-2 text-lg font-bold text-gray-900">{formatRupeeCompact((index + 1) * 125000)}</p>
                        <p className="mt-1 text-xs text-gray-500">Month aggregate preview</p>
                      </div>
                    ))}
                  </div>
                </PreviewBlock>
              </div>
            )}

            {activeTab === 'forms' && (
              <div className="space-y-5">
                <PreviewBlock
                  title="Form Controls"
                caption="Input, select, textarea, checkbox, and toggle styling will be standardized here before rollout."
              >
                <div className="grid gap-4 lg:grid-cols-2">
                  <FormField label="Text Input" helper="Primary text field shell">
                    <input className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-green-500 focus:outline-none" placeholder="Enter value" />
                  </FormField>
                  <FormField label="Select Field" helper="Shared select control shell">
                    <button type="button" className="inline-flex w-full items-center justify-between rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm">
                      Select option
                      <ChevronDown size={16} />
                    </button>
                  </FormField>
                  <FormField label="Textarea" helper="Reason / notes / comments style">
                    <textarea className="min-h-[120px] w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-green-500 focus:outline-none" placeholder="Write notes..." />
                  </FormField>
                  <div className="space-y-3 rounded-2xl border border-gray-100 bg-gray-50 p-4">
                    <label className="flex items-center gap-3 text-sm text-gray-700">
                      <input type="checkbox" className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500" defaultChecked />
                      Checkbox example
                    </label>
                    <label className="flex items-center gap-3 text-sm text-gray-700">
                      <input type="radio" className="h-4 w-4 border-gray-300 text-green-600 focus:ring-green-500" name="template-radio" defaultChecked />
                      Radio example
                    </label>
                  </div>
                </div>
                </PreviewBlock>
              </div>
            )}

            {activeTab === 'popups' && (
              <div className="space-y-5">
                <PreviewBlock
                  title="Popup Action Pattern"
                  caption="Every popup follows the same action logic: Apply keeps the current field values and adds rows into the pending list below; Save commits the changes and closes the popup."
                >
                  <div className="mx-auto max-w-3xl rounded-3xl border border-gray-200 bg-white shadow-sm">
                    <div className="border-b border-gray-100 px-5 py-4">
                      <h4 className="text-base font-semibold text-gray-800">Popup Preview</h4>
                      <p className="mt-1 text-sm text-gray-500">Example for Hours, Holidays, Salary entries, and roster-style batch add screens.</p>
                    </div>
                    <div className="space-y-4 p-5">
                      <div className="grid gap-4 md:grid-cols-2">
                        <FormField label="Field One">
                          <input className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-700" placeholder="Enter value" />
                        </FormField>
                        <FormField label="Field Two">
                          <input className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-700" placeholder="Enter value" />
                        </FormField>
                      </div>
                      <div className="flex justify-end">
                        <button type="button" className="rounded-xl border border-green-200 bg-green-50 px-4 py-2 text-sm font-semibold text-green-700">Apply</button>
                      </div>
                      <div className="rounded-2xl border border-gray-200 bg-white p-4">
                        <p className="text-sm font-semibold text-gray-800">Pending rows preview</p>
                        <div className="mt-3 overflow-x-auto rounded-2xl border border-gray-200">
                          <table className="min-w-full bg-white text-sm">
                            <thead className="bg-gray-50">
                              <tr>
                                <TableHeaderCell label="Item" />
                                <TableHeaderCell label="Date" />
                                <TableHeaderCell label="Status" />
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              <tr>
                                <td className="px-4 py-3 text-gray-800">Sample row</td>
                                <td className="px-4 py-3 text-gray-600">{formatWeekdayDate('Mon', 11, 'May', 2026)}</td>
                                <td className="px-4 py-3"><StatusChip tone="warning">Pending Save</StatusChip></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div className="flex justify-end gap-2 border-t border-gray-100 pt-4">
                        <button type="button" className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700">Cancel</button>
                        <button type="button" className="rounded-xl bg-green-600 px-4 py-2 text-sm font-semibold text-white">Save All</button>
                      </div>
                    </div>
                  </div>
                </PreviewBlock>
              </div>
            )}

            {activeTab === 'feedback' && (
            <div className="space-y-5">
              <PreviewBlock
                title="Feedback, Empty, and Alert States"
                caption="These states will be normalized so every screen handles success, warning, error, and empty data consistently."
              >
                <div className="grid gap-4 xl:grid-cols-3">
                  <div className="rounded-2xl border border-green-200 bg-green-50 p-4">
                    <div className="flex items-center gap-2 text-green-700">
                      <Check size={18} />
                      <p className="font-semibold">Success state</p>
                    </div>
                    <p className="mt-2 text-sm text-green-700">Saved and synchronized locally.</p>
                  </div>
                  <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
                    <div className="flex items-center gap-2 text-amber-800">
                      <AlertCircle size={18} />
                      <p className="font-semibold">Warning state</p>
                    </div>
                    <p className="mt-2 text-sm text-amber-700">Review before pushing to production.</p>
                  </div>
                  <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
                    <div className="flex items-center gap-2 text-red-700">
                      <XCircle size={18} />
                      <p className="font-semibold">Error state</p>
                    </div>
                    <p className="mt-2 text-sm text-red-700">Server validation failed.</p>
                  </div>
                </div>

                <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-6 py-10 text-center">
                  <Info size={22} className="mx-auto text-gray-400" />
                  <h4 className="mt-3 text-sm font-semibold text-gray-700">Empty State Example</h4>
                  <p className="mt-1 text-sm text-gray-500">No records match the current filters.</p>
                </div>
              </PreviewBlock>
            </div>
          )}

            {activeTab === 'flow' && <div className="mt-5 rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-4 text-xs text-gray-500">Next after approval: reorder real pages to this same skeleton before deeper visual cleanup.</div>}
            {activeTab === 'filters' && <div className="mt-5 rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-4 text-xs text-gray-500">Next after approval: wire the approved filter shell into the real pages one by one.</div>}
            {activeTab === 'cards' && <div className="mt-5 rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-4 text-xs text-gray-500">Next after approval: migrate summary cards screen by screen without changing business logic.</div>}
            {activeTab === 'tables' && <div className="mt-5 rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-4 text-xs text-gray-500">Next after approval: replace report/search/table shells while preserving sorting and filtering behavior.</div>}
            {activeTab === 'calendar' && <div className="mt-5 rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-4 text-xs text-gray-500">Next after approval: standardize daily cards, holiday styling, today state, and past/future empty states.</div>}
            {activeTab === 'forms' && <div className="mt-5 rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-4 text-xs text-gray-500">Next after approval: move modal forms and page forms onto one reusable input system.</div>}
            {activeTab === 'popups' && <div className="mt-5 rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-4 text-xs text-gray-500">Next after approval: apply this same Apply-then-Save popup behavior to every add/edit batch popup in the app.</div>}
            {activeTab === 'feedback' && <div className="mt-5 rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-4 text-xs text-gray-500">Next after approval: standardize alerts, empty states, and loading shells.</div>}
        </div>
      </div>
    </div>
  );
};

export default UITemplates;
